import { ADMIN_VERSION_KEY } from '@/lib/preprod/constants';

const CDN_BASE = '/admin-frontend';

type ManifestEntry = {
  file: string;
  name?: string;
  isDynamicEntry?: boolean;
  css?: string[];
};

export function checkVersionParam(): boolean {
  const params = new URLSearchParams(window.location.search);
  const version = params.get('version');
  if (version) {
    localStorage.setItem(ADMIN_VERSION_KEY, version);
    params.delete('version');
    const query = params.toString();
    const url =
      window.location.pathname +
      (query ? `?${query}` : '') +
      window.location.hash;
    window.location.replace(url);
    return true;
  }
  return false;
}

export function getPreprodVersion(): string | null {
  return localStorage.getItem(ADMIN_VERSION_KEY);
}

export async function loadPrerelease(version: string) {
  try {
    const baseUrl = `${CDN_BASE}/${version}`;

    // Fetch vite manifest to discover entry filenames
    const res = await fetch(`${baseUrl}/.vite/manifest.json`);
    if (!res.ok) throw new Error(`Manifest fetch failed: ${res.status}`);
    const manifest: Record<string, ManifestEntry> = await res.json();

    // Find the app chunk by name — Vite keys dynamic imports by internal chunk name
    const appEntry = Object.values(manifest).find(
      (entry) => entry.name === 'app' && entry.isDynamicEntry
    );
    if (!appEntry) throw new Error('App entry not found in manifest');

    // Remove production CSS/modulepreload links to avoid style conflicts
    for (const el of document.querySelectorAll(
      'link[rel="stylesheet"][href*="/admin-frontend/"]'
    )) {
      el.remove();
    }
    for (const el of document.querySelectorAll('link[rel="modulepreload"]')) {
      el.remove();
    }

    // Inject prerelease CSS
    if (appEntry.css) {
      for (const cssFile of appEntry.css) {
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = `${baseUrl}/${cssFile}`;
        document.head.appendChild(link);
      }
    }

    // Dynamic import the prerelease app entry from CDN
    await import(/* @vite-ignore */ `${baseUrl}/${appEntry.file}`);
  } catch (err) {
    // biome-ignore lint/suspicious/noConsole: intentional error logging for prerelease failures
    console.error('[admin-prerelease] Failed to load version:', version, err);
    localStorage.removeItem(ADMIN_VERSION_KEY);
    window.location.reload();
  }
}
